import {
  Drawer,
  Empty,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Table,
  Popconfirm,
  Descriptions,
} from "antd";
import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { Button, Icon, Row } from "../../../components/Component";
import { Alert, Col, Spinner } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormCheckbox from "../../form-components/CheckboxComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import { tagRender } from "../projects/projectCreation";
import debounce from "lodash.debounce";
import { ReactTyped } from "react-typed";
import FormDatePicker from "../../form-components/DatePicker";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { PlusSquareOutlined } from "@ant-design/icons";
import { nanoid } from "@reduxjs/toolkit";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import { projectsApis } from "../../../api/projects";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { activityApis } from "../../../api/Activity";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { pmsMasterApis } from "../../../api/PmsMaster";
import { ActivityInDetailModal } from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import useSequenceNumbering from "../../../hooks/useSequenceNumbering";
import {
  SequenceInput,
  SequenceConfigModal,
} from "../../../components/sequence";

export const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}: any) => {
  const {
    boqTargetId,
    targetId,
    targetLineId,
    materialId,
    categoryId,
    uomId,
    boqLineId,
  } = record || {};
  const lineKeyPart = targetLineId ?? targetId;
  const key =
    boqTargetId +
    "~~" +
    lineKeyPart +
    "~~" +
    categoryId +
    "~~" +
    materialId +
    "~~" +
    uomId +
    (boqLineId ? "~~" + boqLineId : "");

  const rules: any = [
    {
      required: true,
      message: `${title} is required`,
    },
    {
      type: "number",
      message: `${title} must be a number`,
    },
    {
      type: "number",
      max: Number(record?.quantity),
      message: `${title} must be less than or equal to ${record?.quantity}`,
    },
    {
      type: "number",
      min: 0.01,
      message: `${title} must be greater than 0`,
    },
  ];
  let pattern: any = undefined;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex + `_${key}`}
          style={{ margin: 0 }}
          rules={rules}
          initialValue={
            record?.allotedQuantity ?? record?.allocateBOQQuantity ?? 1
          }
        >
          <InputNumber
            className="w-100"
            pattern={pattern}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            parser={(value: any) => value.replace(/\$\s?|(,*)/g, "")}
            onKeyPress={(event) => {
              const regex = /^[0-9\b\.]+$/;
              if (!regex.test(event.key)) {
                event.preventDefault();
              }
            }}
            step={0.01}
            min={0.01}
          />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export type BoqLines = {
  boqTargetId: Number;
  boqTargetNumber: string;
  lines: {
    targetId: Number;
    targetLineId: Number;
    materialId: Number;
    uomId: Number;
    uomName: string;
    materialName: string;
    categoryId: Number;
    categoryName: string;
    quantity: Number;
  }[];
};

const TabsList = [
  {
    tabKey: "activityInformation",
    label: "Activity Information",
  },
  // "Allocate Materials" tab removed — materials come from norms, not BOQ.
  {
    tabKey: "allocateResources",
    label: "Allocate Resource",
  },
];
export type ActivityFlagEnum = "Internal" | "External";

export interface ResourceType {
  activityResourceId?: number | undefined;
  laborTypeId?: number | string;
  labourTypeName?: string;
  laborCategoryId?: number | string;
  laborCategory?: string;
}

export interface ActivityCreationProps {
  projectId?: Number;
  activityId?: Number; // not included in api
  phaseId?: Number;
  activityName?: string;
  activityOwnerId: Number;
  activityWeightage: Number;
  activityBudgetLineId: Number;
  activityStartDate: string;
  activityEndDate: string;
  flag: ActivityFlagEnum;
  activityTags: {
    tagName: string;
  }[];
  activityBoqs: {
    boqTargetId: Number;
    boqTargetLineId: Number;
    materialId: Number;
    categoryId: Number;
    allotedQuantity: Number;
  }[];
  activityResources: {
    laborTypeId: Number;
    laborName?: string;
    laborRole?: string;
    phoneNumber?: string;
  }[];
}

const ActivityCreation: FC<{
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  refreshData: () => void;
  // Page mode renders inline (inside ActivityCreatePage / ProjectPageShell)
  // with Save/Cancel in a sticky footer instead of the drawer header.
  // Context still comes from the ActivityInDetailModal Redux slice, which the
  // page wrapper dispatches before mounting this component.
  mode?: "drawer" | "page";
  onCreated?: () => void;
  onCancel?: () => void;
}> = ({
  modalOpen,
  setModalOpen,
  refreshData,
  mode = "drawer",
  onCreated,
  onCancel,
}) => {
  const isPageMode = mode === "page";
  const {
    data: {
      id: activityId,
      open: activityOpen,
      projectResponse: {
        id: projectId,
        isLocked: lock,
        lockedBy: lockedBy,
        lockedByName: lockedByUserName,
        updatedDate: lockedOn,
        plannedStartDate: projectStartDate,
        plannedEndDate: projectEndDate,
      },
      phaseResponse: {
        id: phaseId,
        status: phaseStatus,
        startDate: phaseStartDate,
        endDate: phaseEndDate,
      },
      extra: { status, createdAt, createdByUserName, endDate, ownerName },
      allowEdit,
    },
  }: { data: ActivityInDetailModal } = useSelector(
    (state: RootState) => state.ActivityInDetailModal
  );

  const [screenSize, setScreenSize] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKeys, setEditingKeys] = useState<string[]>([]);
  const [form] = Form.useForm();
  const isEditing = (record: any) => editingKeys.includes(record.key);

  const [activeTab, setActiveTab] = useState("activityInformation");
  const [tags, setTags] = useState<string[]>([]);
  const [options, setOptions] = useState<
    { label: string; value: string | number }[]
  >([]);
  const [labourTypesList, setLabourTypesList] = useState<SelectOption[]>([]);
  const [laborCategoriesList, setLaborCategoriesList] = useState<
    SelectOption[]
  >([]);
  const [projectUsers, setProjectUsers] = useState<SelectOption[]>([]);
  const [projectTagsList, setProjectTagsList] = useState<any[]>([]);
  const [budgetLines, setBudgetLines] = useState<SelectOption[]>([]);
  const lastFetchedProjectIdForTagsRef = useRef<string | number | null>(null);
  const [boqLines, setBoqLines] = useState<BoqLines[]>([]);
  const [globalError, setGlobalError] = useState<string | undefined>(undefined);
  const [remainingWeightage, setRemainingWeightage] = useState<number | null>(
    null
  );

  // Budget allocation state management
  const [selectedBudgetLine, setSelectedBudgetLine] = useState<{
    lineId: number;
    headerId: number;
    budgetId: string;
    budgetLineName: string;
    budgetValue: number;
    allocatedLineValue: number;
  } | null>(null);
  const [selectedBudgetValue, setSelectedBudgetValue] = useState<number | null>(
    null
  );

  const [resourceModal, setResourceModal] = useState<{
    open: boolean;
    title: "Add New Resource" | "Edit Resource";
    data: ResourceType;
    index: number | undefined;
  }>({
    open: false,
    title: "Add New Resource",
    data: {
      type: "Labour",
    } as ResourceType,
    index: undefined,
  });
  const methods = useForm<{
    activityName: string;
    cloneActivity: boolean;
    activityStartDate: any;
    activityEndDate: any;
    projectId: number | string;
    activityId?: number | string;
    activityBudgetLineId?: number | string;
    flag?: string | number;
    activityOwnerId?: number | string;
    tags?: string[];
    boqLineIds: number[];
    allocatedResources: ResourceType[];
  }>({
    mode: "onChange",
    defaultValues: {
      flag: "Internal",
    },
  });
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { isDirty },
  } = methods;

  // Activity numbering: auto-generate when an "Activities" sequence is
  // configured for this project, otherwise fall back to manual entry so
  // creation isn't blocked.
  const {
    numberingMode,
    showNumberingModal,
    tempNumberingMode,
    setTempNumberingMode,
    activeSequences,
    currentSequence,
    canAuto,
    fetchSequenceAccess,
    openModal,
    closeModal,
    saveModalSelection,
  } = useSequenceNumbering({
    moduleName: "Activities",
    fieldName: "activitySequenceId",
    level: "Project",
    projectId: projectId as number | undefined,
    setValue: setValue as any,
    getValues: getValues as any,
  });
  const [boqMaterialsError, setBoqMaterialsError] = useState<
    string | undefined
  >(undefined);
  const [allocatedResourcesError, setAllocatedResourcesError] = useState<
    string | undefined
  >(undefined);
  const materialAllocateErrorsRef = useRef<HTMLDivElement>(null);
  const resourceAllocateErrorsRef = useRef<HTMLDivElement>(null);
  const { fields, append, update, remove }: any = useFieldArray({
    control,
    name: "allocatedResources",
  });
  const cloneCheckboxWatch = watch("cloneActivity");
  const startDateWatch = watch("activityStartDate");
  const endDateWatch = watch("activityEndDate");
  const activityOwnerIdWatch = watch("activityOwnerId");
  const boqLineIdsWatch = watch("boqLineIds");
  const budgetLineIdWatch = watch("activityBudgetLineId");

  const resourcesMethods = useForm<ResourceType>({
    mode: "onChange",
    // defaultValues: {
    // },
  });

  const {
    control: resourcesControl,
    handleSubmit: resourcesHandleSubmit,
    setValue: resourcesSetValue,
    watch: resourcesWatch,
    reset: resourcesReset,
    formState: { errors: resourcesErrors, dirtyFields: resourcesDirtyFields },
  } = resourcesMethods;
  const [resourceWarning, setResourceWarning] = useState<{
    index: number | undefined;
    message: string | undefined;
  }>({
    index: undefined,
    message: undefined,
  });

  const fetchLabourTypesList = async () => {
    try {
      const response = await pmsMasterApis.labourTypesList();
      const { data: optionsArray, status } = response;
      if (status) {
        setLabourTypesList(
          optionsArray?.map((item: any) => ({
            value: item?.laborTypeId,
            label: item?.laborType,
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchLaborCategoriesList = async () => {
    try {
      const response = await pmsMasterApis.labourCatgoriesList();
      const { data: optionsArray, status } = response;
      if (status) {
        setLaborCategoriesList(
          optionsArray?.map((item: any) => ({
            value: item?.laborCategoryId,
            label: item?.laborCategory,
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const validateResources = (source: ResourceType[], target: ResourceType) => {
    try {
      const { laborTypeId, laborCategoryId } = target;

      for (let i = 0; i < source?.length; i++) {
        const resource = source[i];
        if (
          Number(resource.laborTypeId) === Number(laborTypeId) &&
          Number(resource.laborCategoryId) === Number(laborCategoryId)
        ) {
          setResourceWarning({
            index: source.length,
            message: "An identical resource entry already exists.",
          });
          return false;
        }
      }
      return true;
    } catch (error) {
      console.log("error", error);
      return false;
    }
  };

  const resourceModalCancel = () => {
    setResourceWarning({ index: undefined, message: undefined });
    resourcesReset({
      laborTypeId: undefined,
      labourTypeName: "",
      laborCategoryId: undefined,
      laborCategory: "",
    });
    setResourceModal({
      open: false,
      title: "Add New Resource",
      data: {
        laborTypeId: undefined,
        labourTypeName: "",
        laborCategoryId: undefined,
        laborCategory: "",
      },
      index: undefined,
    });
  };

  const fetchProjectUsers = async () => {
    try {
      const response: any = await projectsApis.fetchProjectUsers(projectId);
      const {
        data: usersList,
        status,
      }: {
        status: boolean;
        data: {
          userId: string;
          userName: string;
          projectUserId: number;
          mobileNumber: string;
          roleName: string;
        }[];
      } = response || {};
      if (status) {
        const formatted = usersList?.map(
          (user: {
            userId: string;
            userName: string;
            mobileNumber: string;
            roleName: string;
          }) => ({
            label: user?.userName,
            value: +user?.userId,
            extra: {
              mobileNumber: user?.mobileNumber,
              roleName: user?.roleName,
            },
          })
        );
        setProjectUsers(formatted);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProjectTags = async () => {
    if (lastFetchedProjectIdForTagsRef.current === projectId) return;
    lastFetchedProjectIdForTagsRef.current = projectId;
    try {
      const response: any = await projectsApis.fetchProjectTags(projectId);
      const {
        data: tagsList,
        status,
      }: {
        status: boolean;
        data: { tagName: string; tagId: number | string }[];
      } = response || {};
      if (status && Array.isArray(tagsList)) {
        setProjectTagsList(tagsList);
      }
    } catch (error) {
      lastFetchedProjectIdForTagsRef.current = null;
      console.log(error);
    }
  };

  const fetchTagSuggestions = (search: string) => {
    const dataToSearch = projectTagsList || [];
    const filtered =
      search && search.length >= 1
        ? dataToSearch.filter((tag) =>
          tag?.tagName?.toLowerCase()?.includes(search?.toLowerCase())
        )
        : dataToSearch;

    const formatted = filtered.map((tag) => ({
      label: tag?.tagName,
      value: tag?.tagId,
    }));

    if (formatted.length === 0 && search && search.length >= 1) {
      setOptions([{ label: search, value: "newTag__" + search }]);
    } else {
      setOptions(formatted);
    }
  };

  const fetchProjectBoqs = async () => {
    try {
      const response: any = await projectsApis.fetchProjectBoqs(projectId);
      const {
        data: boqLines,
        status,
      }: {
        status: boolean;
        data: BoqLines[];
      } = response || {};
      if (status) {
        setBoqLines(boqLines);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchBudgetLines = async (startDate?: any, endDate?: any) => {
    try {
      const params: any = {
        bom: "N",
      };

      // Add date parameters if both dates are provided
      if (startDate && endDate) {
        params.startDate = startDate.format("YYYY-MM-DD");
        params.endDate = endDate.format("YYYY-MM-DD");
      }

      const response: any = await projectsApis.fetchBudgetLines(
        projectId,
        params
      );
      const {
        data: bgtLines,
        status,
      }: {
        status: boolean;
        data: {
          lineId: Number;
          headerId: Number;
          budgetId: string;
          budgetLineName: string;
          budgetValue: Number;
        }[];
      } = response || {};
      if (status) {
        setBudgetLines(
          bgtLines?.map((line: any) => ({
            label: line?.budgetLineName,
            value: line?.lineId,
            extra: {
              budgetValue: line?.budgetValue,
              allocatedLineValue: line?.allocatedLineValue,
              budgetId: line?.budgetId,
              budgetLineName: line?.budgetLineName,
            },
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchActivityWeightages = async () => {
    try {
      const response: any = await activityApis.getPhaseActivityWeightage(
        Number(projectId),
        phaseId ? Number(phaseId) : undefined
      );
      const {
        data: list,
        status,
      }: {
        status: boolean;
        data: { phaseId: number; overallActivityWeightage: number }[];
      } = response || {};
      if (status) {
        const phaseIdNum = phaseId ? Number(phaseId) : undefined;
        const matched =
          (list || []).find(
            (item) => Number(item?.phaseId) === phaseIdNum
          ) || (list || [])[0];
        const used = Number(matched?.overallActivityWeightage) || 0;
        setRemainingWeightage(Math.max(0, 100 - used));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchLabourTypesList();
      fetchLaborCategoriesList();
      fetchProjectUsers();
      fetchProjectTags();
      fetchProjectBoqs();
      fetchActivityWeightages();
      // Don't fetch budget lines initially - wait for dates to be selected
      // fetchBudgetLines();
    }
  }, [projectId, phaseId]);

  useEffect(() => {
    if (projectId && modalOpen) {
      fetchSequenceAccess();
    }
  }, [projectId, modalOpen, fetchSequenceAccess]);

  // Populate Tags dropdown options when projectTagsList loads
  useEffect(() => {
    if (projectTagsList?.length > 0) {
      fetchTagSuggestions("");
    }
  }, [projectTagsList]);

  // Refetch budget lines when dates change
  useEffect(() => {
    if (projectId && startDateWatch && endDateWatch) {
      fetchBudgetLines(startDateWatch, endDateWatch);
    }
  }, [projectId, startDateWatch, endDateWatch]);

  // Handle budget line selection to update budget value
  useEffect(() => {
    if (budgetLineIdWatch && budgetLines.length > 0) {
      const selectedBudget = budgetLines.find(
        (budget) => budget.value === budgetLineIdWatch
      );
      if (selectedBudget?.extra) {
        setSelectedBudgetValue(selectedBudget.extra.budgetValue);
        setSelectedBudgetLine({
          lineId: selectedBudget.value as number,
          headerId: 0, // Not needed for display
          budgetId: selectedBudget.extra.budgetId,
          budgetLineName: selectedBudget.extra.budgetLineName,
          budgetValue: selectedBudget.extra.budgetValue,
          allocatedLineValue: selectedBudget.extra.allocatedLineValue,
        });
      }
    } else {
      setSelectedBudgetValue(null);
      setSelectedBudgetLine(null);
    }
  }, [budgetLineIdWatch, budgetLines]);

  const filteredData: any = [];
  boqLineIdsWatch?.forEach((id) => {
    const boqLine: any = boqLines?.find((item: any) => item.boqTargetId == id);
    const { lines, boqTargetId, boqTargetNumber, boqTargetName, boqTitle } =
      boqLine;
    if (boqLine) {
      const boqLines = lines?.map((line: any) => {
        const {
          targetId,
          targetLineId,
          materialId,
          categoryId,
          uomId,
          boqLineId,
          boqLineName,
        } = line;
        const lineKeyPart = targetLineId ?? targetId;
        return {
          ...line,
          boqTargetId,
          boqTargetNumber,
          boqTitle,
          boqLineId,
          boqLineName,
          key:
            boqTargetId +
            "~~" +
            lineKeyPart +
            "~~" +
            categoryId +
            "~~" +
            materialId +
            "~~" +
            uomId +
            (boqLineId ? "~~" + boqLineId : ""),
        };
      });
      filteredData.push(...boqLines);
    }
  });
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setBoqMaterialsError(undefined);
    if (!newSelectedRowKeys) {
      editingKeys.forEach((key) => {
        form.setFieldsValue({ [`allocateBOQQuantity_${key}`]: 1 });
      });
      setEditingKeys(() => []);
      form.resetFields();
    }
    const unSelectedRowKeys = selectedRowKeys.filter(
      (key) => !newSelectedRowKeys.includes(key)
    );
    unSelectedRowKeys?.forEach((key) => {
      form.setFieldsValue({ [`allocateBOQQuantity_${key}`]: 1 });
    });
    const editableRowKeys: any = [];
    newSelectedRowKeys?.forEach((key) => {
      editableRowKeys.push(key);
    });
    setEditingKeys(editableRowKeys);
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  const debouncedFetch = debounce(fetchTagSuggestions, 100);
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // const validateAllocateMaterials = () => {
  //   try {
  //     if (selectedRowKeys?.length == 0) {
  //       return {
  //         status: false,
  //         message: "Please select at least one material and allocate quantity",
  //       };
  //     }
  //     return {
  //       status: true,
  //       message: "",
  //     };
  //   } catch (error) {
  //     return {
  //       status: false,
  //       message: "Something went wrong",
  //     };
  //   }
  // };

  const submitNewActivity = async (data: any) => {
    try {
      setDisableSubmit(true);
      // const {
      //   status: materialAllocationStatus,
      //   message: materialAllocationMessage,
      // } = validateAllocateMaterials();
      // if (!materialAllocationStatus) {
      //   materialAllocateErrorsRef?.current?.scrollIntoView({
      //     behavior: "smooth",
      //   });
      //   setActiveTab("allocateMaterials");
      //   setBoqMaterialsError(materialAllocationMessage);
      //   return;
      // }

      const values = await form.validateFields();
      const { errorFields } = values;
      if (errorFields) {
        setActiveTab("activityInformation");
        return;
      }

      // Recalculate filteredData to get targetLineId
      const currentFilteredData: any = [];
      boqLineIdsWatch?.forEach((id) => {
        const boqLine: any = boqLines?.find(
          (item: any) => item.boqTargetId == id
        );
        const { lines, boqTargetId, boqTargetNumber, boqTargetName, boqTitle } =
          boqLine;
        if (boqLine) {
          const boqLines = lines?.map((line: any) => {
            const {
              targetId,
              targetLineId,
              materialId,
              categoryId,
              uomId,
              boqLineId,
              boqLineName,
            } = line;
            const lineKeyPart = targetLineId ?? targetId;
            return {
              ...line,
              boqTargetId,
              boqTargetNumber,
              boqTitle,
              boqLineId,
              boqLineName,
              key:
                boqTargetId +
                "~~" +
                lineKeyPart +
                "~~" +
                categoryId +
                "~~" +
                materialId +
                "~~" +
                uomId +
                (boqLineId ? "~~" + boqLineId : ""),
            };
          });
          currentFilteredData.push(...boqLines);
        }
      });

      const updatedActivityBoqs: any = Object.keys(values).map((key) => {
        // Remove the prefix (e.g., "allocateBOQQuantity_") to get the actual key
        const actualKey = key.split("_")[1];
        const keyParts = actualKey.split("~~");
        const [
          boqTargetId,
          targetId,
          categoryId,
          materialId,
          uomId,
          boqLineId,
        ] = keyParts;
        // Find the targetLineId and boqLineId from the currentFilteredData
        const lineData = currentFilteredData.find(
          (line: any) => line.key === actualKey
        );
        return {
          boqTargetId: +boqTargetId,
          boqTargetLineId: lineData?.targetLineId,
          boqLineId: lineData?.boqLineId || boqLineId || undefined,
          materialId: +materialId,
          categoryId: +categoryId,
          targetId: +targetId,
          uomId: +uomId,
          allotedQuantity: values[key],
        };
      });
      // if (fields?.length == 0) {
      //   setAllocatedResourcesError(
      //     "Please add at least one resource to the activity"
      //   );
      //   resourceAllocateErrorsRef?.current?.scrollIntoView({
      //     behavior: "smooth",
      //   });
      //   setActiveTab("allocateResources");
      //   return;
      // }

      const {
        cloneActivity,
        flag,
        activityName,
        activityOwnerId,
        activityBudgetLineId,
        activityStartDate,
        activityEndDate,
        activityWeightage,
      } = data;
      const activityResources: any = [];
      fields?.forEach((resource: any) => {
        activityResources.push({
          laborTypeId: resource?.laborTypeId,
          laborCategoryId: resource?.laborCategoryId,
        });
      });
      const payload: Partial<ActivityCreationProps> = {
        projectId: +(projectId ?? 0),
        phaseId: +(phaseId ?? 0),
        activityName,
        activityOwnerId,
        activityWeightage: +activityWeightage,
        activityBudgetLineId,
        flag: flag,
        activityTags: tags?.map((tag: any) => {
          if (typeof tag?.value === "string") {
            return {
              tagName: tag?.label,
            };
          }
          return {
            tagId: Number(tag?.value),
            tagName: tag?.label,
          };
        }),
        // BOQ delinked — materials come from norms, not activity BOQ.
        activityBoqs: [],
        activityResources,
      };
      if (activityStartDate)
        payload.activityStartDate =
          dayjs(activityStartDate).format("YYYY-MM-DD");
      if (activityEndDate)
        payload.activityEndDate = dayjs(activityEndDate).format("YYYY-MM-DD");
      // Send the activity number only when entered manually (auto mode lets
      // the server assign it from the sequence master).
      if (numberingMode === "manual" && data.activitySequenceId) {
        (payload as any).activitySequenceId = data.activitySequenceId;
      }
      if (cloneActivity) {
        payload.projectId = data.projectId;
        payload.activityId = data.activityId;
      }
      const response: ApiResponse = await activityApis.createActivity(payload);
      const { data: activity, status, error, message } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: activity?.message,
          confirmButtonText: "Ok",
        });
        reset();
        setTags([]);
        refreshData();
        if (isPageMode) {
          onCreated?.();
        } else {
          setModalOpen(false);
        }
      } else {
        setGlobalError(error + ": " + message);
        setTimeout(() => {
          setGlobalError(undefined);
        }, 50000);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDisableSubmit(false);
    }
  };

  // Shared close logic for the header (drawer) and footer (page) Cancel
  // buttons. Confirms discard only when the form is dirty, then routes to the
  // right close mechanism for the current mode.
  const handleCancelCreation = () => {
    const close = () => {
      if (isPageMode) {
        onCancel?.();
      } else {
        setModalOpen(false);
      }
    };
    if (isDirty) {
      Swal.fire({
        title: "Are you sure?",
        text: "You have unsaved changes",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, discard changes!",
      }).then((result) => {
        if (result.isConfirmed) {
          reset();
          setTags([]);
          refreshData();
          close();
        }
      });
    } else {
      close();
    }
  };

  const validateSubmit = (ErrorsData: any) => {
    try {
      const errorKeys = Object.keys(ErrorsData);
      if (
        [
          "activityOwnerId",
          "activityStartDate",
          "activityEndDate",
          "activityBudgetLineId",
          "activityWeightage",
        ]?.some((key) => errorKeys?.includes(key))
      ) {
        setActiveTab("activityInformation");
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  return (
    <div className="mt-2">
      <Alert
        isOpen={!!globalError}
        color="danger"
        className="start-0 w-100 py-1 mt-2"
        style={{
          top: 0,
          textAlign: "left",
          fontSize: "14px",
          cursor: "default",
        }}
      >
        <Icon name={"info"} style={{ top: "0.6rem" }} />
        <ReactTyped
          strings={[globalError || ""]}
          typeSpeed={10}
          showCursor={false}
        />
      </Alert>
      {!isPageMode && (
        <div className="d-flex justify-content-between align-items-center mb-2 ">
          <h5 className="w-100 d-flex  align-items-center">
            New Activity Creation
          </h5>
          <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 pe-2">
            <li>
              {disableSubmit ? (
                <Button color="primary" type="button">
                  <Spinner size="sm" color="light" />
                </Button>
              ) : (
                <Button
                  className="btn btn-primary btn-sm"
                  type="button"
                  color="primary"
                  onClick={handleSubmit(submitNewActivity, validateSubmit)}
                >
                  <Icon name={"save"} />
                  <span>Save</span>
                </Button>
              )}
            </li>
            <li>
              <Button
                className="btn btn-secondary btn-sm"
                type="button"
                disabled={disableSubmit}
                color="secondary"
                onClick={handleCancelCreation}
              >
                <span>Cancel</span>
              </Button>
            </li>
          </ul>
        </div>
      )}
      {contextHolder}
      {modalOpen && (
        <FormProvider {...methods}>
          <form
            className="content clearfix mt-2"
            onSubmit={handleSubmit((data) => {
              console.log("data", data);
            })}
          >
            <div className="row g-3">
              <div className="col-4 ">
                <FormInput
                  name="activityName"
                  label="Activity Name"
                  required={true}
                  type="text"
                  placeholder="Enter Activity Name"
                />
                <FormCheckbox
                  name="cloneActivity"
                  optionLabel="Clone From Existing Project"
                  noGap={true}
                  size={"small"}
                  className="mt-1"
                  disabled
                />
              </div>
              <div className="col-4">
                <SequenceInput
                  name="activitySequenceId"
                  label="Activity Number"
                  numberingMode={numberingMode}
                  onSettingsClick={openModal}
                />
              </div>
              {/* {cloneCheckboxWatch ? (
                <>
                  <div className="col-4">
                    <FormSelect
                      name="projectId"
                      label="Project"
                      required={true}
                      placeholder="Select Project"
                      options={[
                        { label: "Project 1", value: 1 },
                        { label: "Project 2", value: 2 },
                        { label: "Project 3", value: 3 },
                      ]}
                    />
                  </div>
                  <div className="col-4">
                    <FormSelect
                      name="activityId"
                      label="Activity"
                      required={true}
                      placeholder="Select Activity"
                      options={[
                        { label: "Activity 1", value: 1 },
                        { label: "Activity 2", value: 2 },
                        { label: "Activity 3", value: 3 },
                      ]}
                    />
                  </div>
                </>
              ) : (
                <></>
              )} */}
            </div>

            {TabsList?.length > 0 && (
              <div className="d-flex align-items-center flex-wrap mb-2 mt-2">
                <ul className="nav nav-tabs nav-tabs-card nav-tabs-xs w-100 p-0 m-0 position-relative ">
                  {TabsList?.map((_tab: any, index: number) => {
                    return (
                      <li
                        className="nav-item"
                        key={_tab?.tabKey}
                        onClick={() => setActiveTab(() => _tab?.tabKey)}
                      >
                        <a
                          onClick={(ev) => {
                            ev.preventDefault();
                          }}
                          className={`nav-link${activeTab === _tab?.tabKey ? " active" : ""
                            }`}
                          href={"#" + _tab?.tabKey}
                        >
                          {_tab?.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
                <div className="tab-content mt-2 w-100">
                  <div
                    key={"activityInformation"}
                    className={`tab-pane${activeTab === "activityInformation" ? " active" : " "
                      }`}
                  >
                    <div className="row g-3">
                      <div className="col-4">
                        <FormSelect
                          name="activityOwnerId"
                          label="Owner"
                          placeholder="Select Owner"
                          options={projectUsers}
                          required={true}
                        />
                      </div>
                      <div className="col-4">
                        <FormDatePicker
                          name="activityStartDate"
                          label="Planned Start Date"
                          minDate={
                            phaseStartDate
                              ? new Date(phaseStartDate)
                              : undefined
                          }
                          maxDate={
                            phaseEndDate ? new Date(phaseEndDate) : undefined
                          }
                          required={true}
                        />
                      </div>
                      <div className="col-4">
                        <FormDatePicker
                          name="activityEndDate"
                          label="Planned End Date"
                          minDate={startDateWatch}
                          disabled={!startDateWatch}
                          maxDate={
                            phaseEndDate ? new Date(phaseEndDate) : undefined
                          }
                          required={true}
                        />
                      </div>
                    </div>
                    <div className="row g-3 mt-1">
                      <div className="col-4">
                        <FormSelect
                          name="activityBudgetLineId"
                          required
                          hideRequiredMark
                          label={
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <span>Budget Line</span>
                              <span style={{ color: "red" }}>*</span>
                              {selectedBudgetLine && (
                                <Popconfirm
                                  placement="right"
                                  title={<></>}
                                  icon={<></>}
                                  showCancel={false}
                                  trigger="hover"
                                  okButtonProps={{
                                    style: {
                                      display: "none",
                                    },
                                  }}
                                  description={
                                    <div className="d-flex flex-column">
                                      <Descriptions
                                        title=""
                                        bordered
                                        column={1}
                                        size="small"
                                        className="custom-description-title"
                                      >
                                        <Descriptions.Item label="Budget Value">
                                          ₹
                                          {selectedBudgetLine.budgetValue?.toLocaleString(
                                            "en-IN"
                                          )}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Allocated Value">
                                          ₹
                                          {selectedBudgetLine.allocatedLineValue?.toLocaleString(
                                            "en-IN"
                                          )}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Remaining">
                                          ₹
                                          {(
                                            selectedBudgetLine.budgetValue -
                                            selectedBudgetLine.allocatedLineValue
                                          )?.toLocaleString("en-IN")}
                                        </Descriptions.Item>
                                      </Descriptions>
                                    </div>
                                  }
                                >
                                  <Icon
                                    name="info"
                                    className="ms-1"
                                    style={{
                                      color: "#1890ff",
                                      cursor: "pointer",
                                      fontSize: "16px",
                                    }}
                                  />
                                </Popconfirm>
                              )}
                            </div>
                          }
                          placeholder={
                            !startDateWatch || !endDateWatch
                              ? "Please select start and end dates first"
                              : "Select Budget Line"
                          }
                          options={budgetLines}
                          disabled={!startDateWatch || !endDateWatch}
                        />
                      </div>
                      <div className="col-4">
                        <FormSelect
                          name="flag"
                          label="Flag"
                          placeholder="Select Flag"
                          options={[
                            { label: "Internal", value: "Internal" },
                            { label: "External", value: "External" },
                          ]}
                        />
                      </div>
                      <div className="col-4">
                        <FormInput
                          name="activityWeightage"
                          label={
                            <span>
                              Weightage
                              {remainingWeightage !== null && (
                                <span
                                  style={{
                                    color: "#52c41a",
                                    marginLeft: "8px",
                                    fontWeight: "normal",
                                  }}
                                >
                                  (Avl: {remainingWeightage}%)
                                </span>
                              )}
                            </span>
                          }
                          placeholder="Enter Weightage %"
                          type="number"
                          pattern={/^(?:100|[1-9][0-9]?)$/}
                          patternErrorMessage="Enter a value between 1 to 100"
                          customValidation={{
                            validate: (value: string) => {
                              if (
                                remainingWeightage === null ||
                                value === undefined ||
                                value === null ||
                                value === ""
                              )
                                return true;
                              const n = Number(value);
                              if (Number.isNaN(n)) return true;
                              return (
                                n <= remainingWeightage ||
                                `Cannot exceed available (${remainingWeightage}%)`
                              );
                            },
                          }}
                        />
                      </div>
                    </div>

                    <div className="row g-3 mt-1">
                      <Col lg="12">
                        <label className="form-label">Tags</label>
                        <Select
                          mode="multiple"
                          style={{ width: "100%" }}
                          placeholder="Type and press enter to create or select a tag"
                          labelInValue
                          value={tags}
                          onSearch={debouncedFetch}
                          onDropdownVisibleChange={(open) => {
                            if (open && projectTagsList?.length > 0) {
                              fetchTagSuggestions("");
                            }
                          }}
                          onChange={(value) => {
                            const latest: any = value?.slice(-1)[0]; // Get the last tag

                            if (latest?.label?.length < 2) {
                              // Check if the last tag length is less than 2 characters
                              messageApi.open({
                                type: "error",
                                content: "Enter at least 2 characters",
                                duration: 5,
                              });
                              setOptions([]);
                              return;
                            }
                            setTags(value);
                          }}
                          options={options}
                          tokenSeparators={[","]}
                          tagRender={tagRender}
                          filterOption={false} // Use custom filtering only
                        />
                      </Col>
                    </div>
                  </div>
                  <div
                    key={"allocateResources"}
                    className={`tab-pane${activeTab === "allocateResources" ? " active" : " "
                      }`}
                  >
                    <span ref={resourceAllocateErrorsRef}></span>
                    <Alert
                      isOpen={!!allocatedResourcesError}
                      color="danger"
                      className="start-0 w-100 py-1 mt-2"
                      style={{
                        top: 0,
                        textAlign: "left",
                        fontSize: "14px",
                        cursor: "default",
                      }}
                    >
                      <Icon name={"info"} style={{ top: "0.6rem" }} />
                      <ReactTyped
                        strings={[allocatedResourcesError || ""]}
                        typeSpeed={10}
                        showCursor={false}
                      />
                    </Alert>
                    <div className="d-flex align-items-end justify-content-between mt-0 mb-3">
                      <h6 className="mb-0"></h6>
                      <a
                        onClick={() => {
                          setResourceModal((pre) => ({
                            ...pre,
                            open: true,
                            title: "Add New Resource",
                          }));
                        }}
                        className="primary"
                        style={{
                          fontSize: 16,
                          cursor: "pointer",
                        }}
                      >
                        <PlusSquareOutlined /> Add Resource
                      </a>
                    </div>

                    <div
                      style={{
                        width: screenSize - 120 + "px",
                        margin: "10px auto",
                      }}
                    >
                      <Table
                        rowKey={nanoid()}
                        columns={[
                          {
                            title: "Labour Type",
                            dataIndex: "labourTypeName",
                            key: "labourTypeName",
                            width: 200,
                          },
                          {
                            title: "Labour Category",
                            dataIndex: "laborCategory",
                            key: "laborCategory",
                            width: 200,
                          },
                          {
                            title: "Action",
                            key: "action",
                            fixed: "right",
                            width: 90,
                            render: (text, record: ResourceType, index) => (
                              <>
                                <Button
                                  size="sm"
                                  type="button"
                                  onClick={() => {
                                    resourcesSetValue(
                                      "laborTypeId",
                                      record?.laborTypeId
                                    );
                                    resourcesSetValue(
                                      "labourTypeName",
                                      record?.labourTypeName
                                    );
                                    resourcesSetValue(
                                      "laborCategoryId",
                                      record?.laborCategoryId
                                    );
                                    resourcesSetValue(
                                      "laborCategory",
                                      record?.laborCategory
                                    );
                                    setResourceModal((pre) => ({
                                      ...pre,
                                      open: true,
                                      title: "Edit Resource",
                                      data: {
                                        ...record,
                                      },
                                      index: index,
                                    }));
                                  }}
                                >
                                  <Icon name="edit" />
                                </Button>
                                <Button
                                  size="sm"
                                  type="button"
                                  onClick={() => {
                                    setAllocatedResourcesError(undefined);
                                    remove(index);
                                  }}
                                >
                                  <Icon name="trash" />
                                </Button>
                              </>
                            ),
                          },
                        ]}
                        dataSource={fields}
                        pagination={false}
                        bordered
                        size="middle"
                        scroll={{
                          x: 1260,
                        }}
                        locale={{
                          emptyText: (
                            <>
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                  <>
                                    <strong>
                                      No Resource Found, Please{" "}
                                      <a
                                        onClick={() => {
                                          setResourceModal((pre) => ({
                                            ...pre,
                                            open: true,
                                            title: "Add New Resource",
                                          }));
                                        }}
                                        className="primary"
                                        style={{
                                          fontSize: 16,
                                          cursor: "pointer",
                                        }}
                                      >
                                        Add Resource.
                                      </a>
                                    </strong>
                                  </>
                                }
                              />
                            </>
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
          <SequenceConfigModal
            moduleName="Activities"
            fieldLabel="Activity Number"
            show={showNumberingModal}
            tempNumberingMode={tempNumberingMode}
            setTempNumberingMode={setTempNumberingMode}
            canAuto={canAuto}
            currentSequence={currentSequence}
            activeSequences={activeSequences}
            onSave={saveModalSelection}
            onClose={closeModal}
          />
        </FormProvider>
      )}
      <Modal
        maskClosable={false}
        title={resourceModal?.title}
        animation={false}
        open={resourceModal?.open}
        footer={null}
        width={"90%"}
        onCancel={() => {
          setResourceModal((pre) => ({
            ...pre,
            open: false,
          }));
        }}
      >
        <FormProvider {...resourcesMethods}>
          <form>
            <div className={` mb-2 p-0 `}>
              <div
                style={{
                  minHeight: "120px",
                }}
              >
                <Row className="w-100 mt-1 mb-1">
                  <Col lg="6">
                    <FormSelect
                      name={`laborTypeId`}
                      label="Labour Type"
                      options={labourTypesList}
                      placeholder="Select Labour Type"
                      required={true}
                      onChange={(e, option) => {
                        resourcesSetValue("labourTypeName", option?.label);
                      }}
                    />
                  </Col>
                  <Col lg="6">
                    <FormSelect
                      name={`laborCategoryId`}
                      label="Labour Category"
                      options={laborCategoriesList}
                      placeholder="Select Labour Category"
                      required={true}
                      onChange={(e, option) => {
                        resourcesSetValue("laborCategory", option?.label);
                      }}
                    />
                  </Col>
                </Row>
              </div>
              <Alert
                isOpen={resourceWarning?.index !== undefined}
                color="danger"
                className="start-0 w-100 py-1 mt-2"
                style={{
                  top: 0,
                  textAlign: "left",
                  fontSize: "14px",
                  cursor: "default",
                }}
              >
                <ReactTyped
                  strings={[resourceWarning?.message || ""]}
                  typeSpeed={15}
                  showCursor={false}
                />
              </Alert>
              <Row className="mt-4">
                <Col lg="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    {resourceModal?.title === "Edit Resource" ? (
                      <li>
                        <Button
                          className="btn btn-primary btn-sm"
                          type="button"
                          color="primary"
                          onClick={resourcesHandleSubmit((data: any) => {
                            setAllocatedResourcesError(undefined);
                            const isValid =
                              fields?.length > 0
                                ? validateResources(
                                  fields?.filter(
                                    (_: any, idx: any) =>
                                      idx != resourceModal?.index
                                  ),
                                  data
                                )
                                : true;
                            if (!isValid) return;

                            const dirtyData = getDirtyFieldsOnly(
                              data,
                              resourcesDirtyFields
                            );
                            if (
                              dirtyData === undefined ||
                              Object.keys(dirtyData).length === 0
                            ) {
                              resourceModalCancel();
                              return;
                            }
                            update(resourceModal?.index, data);
                            resourceModalCancel();
                          })}
                        >
                          Update
                        </Button>
                      </li>
                    ) : (
                      <li>
                        <Button
                          className="btn btn-primary btn-sm"
                          type="button"
                          color="primary"
                          onClick={resourcesHandleSubmit((data: any) => {
                            setAllocatedResourcesError(undefined);
                            const isValid =
                              fields?.length > 0
                                ? validateResources(fields, data)
                                : true;
                            if (!isValid) return;
                            append(data);
                            resourceModalCancel();
                          })}
                        >
                          Add
                        </Button>
                      </li>
                    )}
                    <li>
                      <a
                        href="#cancel"
                        onClick={(ev) => {
                          ev.preventDefault();
                          resourceModalCancel();
                        }}
                        className="link link-light"
                      >
                        Cancel
                      </a>
                    </li>
                  </ul>
                </Col>
              </Row>
            </div>
          </form>
        </FormProvider>
      </Modal>

      {isPageMode && (
        <div className="activity-create-footer">
          <Button
            className="btn btn-secondary btn-sm"
            type="button"
            disabled={disableSubmit}
            color="secondary"
            onClick={handleCancelCreation}
          >
            <span>Cancel</span>
          </Button>
          {disableSubmit ? (
            <Button color="primary" type="button">
              <Spinner size="sm" color="light" />
            </Button>
          ) : (
            <Button
              className="btn btn-primary btn-sm"
              type="button"
              color="primary"
              onClick={handleSubmit(submitNewActivity, validateSubmit)}
            >
              <Icon name={"save"} />
              <span>Save</span>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityCreation;
